import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";
import { scrollToSection } from "../config";
import { useSite, waLink, resolveMedia } from "../siteContext";
import { useLang, pick } from "../i18n";
import { PILLARS } from "./Pillars";

const ease = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const settings = useSite();
  const { lang, s } = useLang();
  const LINES = [
    [{ text: pick(lang, settings, "hero_line1"), gold: false }],
    [
      { text: pick(lang, settings, "hero_line2"), gold: false },
      { text: pick(lang, settings, "hero_line3"), gold: true },
    ],
  ];
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-8%"]);
  const heroBg = resolveMedia(settings.hero_bg) || "/maya-desk-hero.webp";
  const strip = (PILLARS[lang] || PILLARS.ar).items;
  const hide = (id) => (settings.hidden_elements || []).includes(id);
  const customBtns = (settings.custom_buttons || []).filter((b) => b.location === "hero");

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative lg:min-h-screen overflow-hidden flex flex-col bg-luxury-black"
    >
      <div className="absolute inset-0 hidden lg:block">
        <motion.img
          data-testid="hero-bg-image"
          src={heroBg}
          alt={s.hero_alt}
          style={{ y: bgY }}
          className="w-full h-[112%] object-cover lg:object-[80%_35%]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1B0E23]/95 via-[#1B0E23]/70 to-transparent pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1B0E23]/60 via-transparent to-[#1B0E23] pointer-events-none" />
      </div>

      <div className="lg:hidden relative pt-[72px] bg-luxury-black">
        <img
          data-testid="hero-bg-image-mobile"
          src={heroBg}
          alt={s.hero_alt}
          className="w-full h-[54vh] object-cover object-[66%_top]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#1B0E23] pointer-events-none" />
      </div>

      <div className="relative flex-1 flex items-center pt-8 pb-14 lg:pt-32">
        <div className="max-w-7xl mx-auto px-4 md:px-8 w-full">
          <div dir="ltr" className="flex">
            <motion.div
              style={{ y: textY }}
              dir={lang === "ar" ? "rtl" : "ltr"}
              className="w-full lg:w-[46%] xl:w-[42%] 2xl:w-[40%] text-center lg:text-start"
            >
              <motion.p
                data-testid="hero-eyebrow"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.8 }}
                className="text-luxury-gold text-sm tracking-[0.35em] mb-8 font-sans"
              >
                {s.hero_eyebrow}
              </motion.p>

              <h1 data-testid="hero-headline" className="font-serif font-bold text-3xl sm:text-4xl lg:text-5xl xl:text-6xl leading-[1.5] sm:leading-[1.45] text-luxury-bone">
                {LINES.map((parts, i) => (
                  <span key={i} className="mask-line pb-[0.3em] -mb-[0.1em]">
                    <motion.span
                      className="inline-block"
                      initial={{ y: "115%" }}
                      animate={{ y: 0 }}
                      transition={{ delay: 0.25 + i * 0.25, duration: 1, ease }}
                    >
                      {parts.map((p, j) => (
                        <span key={j} className={p.gold ? "gold-gradient-text italic" : ""}>
                          {p.text}{j < parts.length - 1 ? " " : ""}
                        </span>
                      ))}
                    </motion.span>
                  </span>
                ))}
              </h1>

              <motion.p
                data-testid="hero-subtext"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1, duration: 0.9 }}
                className="mt-8 text-base md:text-lg text-luxury-gray leading-relaxed max-w-xl mx-auto lg:mx-0"
              >
                {pick(lang, settings, "hero_subtext")}
              </motion.p>

              {(!hide("hero_primary") || !hide("hero_secondary") || customBtns.length > 0) && (
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2, duration: 0.9 }}
                className="mt-10 flex flex-wrap items-center justify-center lg:justify-start gap-4"
              >
                {!hide("hero_primary") && (
                <button
                  data-testid="hero-start-button"
                  onClick={() => scrollToSection("contact")}
                  className="group inline-flex items-center gap-3 bg-luxury-gold text-luxury-black font-bold text-lg px-10 py-5 rounded-full shadow-[0_12px_45px_-8px_rgba(209,160,92,0.55)] hover:bg-luxury-goldLight hover:shadow-[0_16px_60px_-6px_rgba(209,160,92,0.7)] hover:-translate-y-0.5 transition-[background-color,box-shadow,transform] duration-300"
                >
                  {s.cta_start}
                  <ArrowDown className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-1" strokeWidth={1.5} />
                </button>
                )}
                {!hide("hero_secondary") && (
                <a
                  data-testid="hero-whatsapp-button"
                  href={waLink(settings, undefined, lang)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 border border-luxury-gold/60 text-luxury-gold px-8 py-4 rounded-full hover:bg-luxury-gold hover:text-luxury-black transition-colors duration-300"
                >
                  <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
                  {s.hero_whatsapp}
                </a>
                )}
                {customBtns.map((b) => (
                  <a
                    key={b.id}
                    data-testid={`hero-custom-btn-${b.id}`}
                    href={b.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={b.style === "outline"
                      ? "inline-flex items-center gap-3 border border-luxury-gold/60 text-luxury-gold px-8 py-4 rounded-full hover:bg-luxury-gold hover:text-luxury-black transition-colors duration-300"
                      : "inline-flex items-center gap-3 bg-luxury-gold text-luxury-black font-semibold px-8 py-4 rounded-full hover:bg-luxury-goldLight transition-colors duration-300"}
                  >
                    {b.label}
                  </a>
                ))}
              </motion.div>
              )}

              {!hide("hero_stats") && (
              <motion.div
                data-testid="hero-stats"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.45, duration: 0.9 }}
                className="mt-14 grid grid-cols-3 gap-6 max-w-xl border-t border-white/15 pt-8"
              >
                {(settings.stats || []).map((st, i) => (
                  <div key={i} data-testid={`hero-stat-${i}`}>
                    <p className="font-serif text-3xl md:text-4xl gold-gradient-text leading-tight">
                      {lang === "en" && st.value_en ? st.value_en : st.value}
                    </p>
                    <p className="text-luxury-gray text-xs md:text-sm mt-2 leading-relaxed">
                      {lang === "en" && st.label_en ? st.label_en : st.label}
                    </p>
                  </div>
                ))}
              </motion.div>
              )}
            </motion.div>
          </div>
        </div>
      </div>

      {!hide("hero_features") && (
      <motion.div
        data-testid="hero-feature-strip"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.7, duration: 0.9 }}
        className="relative border-t border-white/10 bg-[#1B0E23]/55 backdrop-blur-md"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-2 lg:grid-cols-4 gap-x-6">
          {strip.map((p, i) => (
            <div key={i} data-testid={`hero-feature-${i}`} className="flex items-center gap-4 py-6">
              <span className="w-11 h-11 rounded-full border border-luxury-gold/40 bg-luxury-gold/10 flex items-center justify-center shrink-0">
                <p.icon className="w-5 h-5 text-luxury-gold" strokeWidth={1.25} />
              </span>
              <p className="text-luxury-bone text-sm leading-snug font-medium">{p.title}</p>
            </div>
          ))}
        </div>
      </motion.div>
      )}
    </section>
  );
};
